import { inject, injectable } from 'inversify'
import { action, computed, makeObservable } from 'mobx'
import { I18nRepository } from './i18n.repository'
import { TranslationsConfig, type Locale } from './i18n.config'

export type LocaleOptionViewModel = {
  code: Locale
  label: string
  flag: string
  isSelected: boolean
}

/**
 * The read/switch side of locale: what a language-switcher component renders,
 * plus the action it fires. Kept as a Presenter (rather than folded into the
 * Repository) because that UI is a deliverable and components inject Presenters
 * only.
 *
 * It is *not* how translations reach the rest of the app: a feature presenter
 * injects I18nRepository and resolves its own strings into its own ViewModel.
 * See the note on I18nRepository.t().
 */
@injectable()
export class I18nPresenter {
  @inject(I18nRepository)
  private i18nRepository!: I18nRepository

  @inject(TranslationsConfig)
  private translationsConfig!: TranslationsConfig

  constructor() {
    makeObservable(this, {
      locale: computed,
      viewModel: computed,
      setLocale: action,
    })
  }

  get locale(): Locale {
    return this.i18nRepository.localePm
  }

  /** Every option pre-resolved, selection already decided (§3.4/§4.3). */
  get viewModel() {
    const current = this.locale

    return {
      // Endonyms stay untranslated (see TranslationsConfig); the group's own
      // accessible name does not, so it comes through t().
      groupLabel: this.i18nRepository.t('shell.language'),
      currentLabel: this.translationsConfig.labelFor(current),
      currentFlag: this.translationsConfig.locales.find((locale) => locale.code === current)!.flag,
      options: this.translationsConfig.locales.map((locale): LocaleOptionViewModel => ({
        code: locale.code,
        label: locale.label,
        flag: locale.flag,
        isSelected: locale.code === current,
      })),
    }
  }

  setLocale = (locale: Locale) => {
    this.i18nRepository.setLocale(locale)
  }
}
